'use client';

import IdGeneratorService from '@/services/id-generator.service';
import { FloatLabel } from 'primereact/floatlabel';
import type { KeyFilterType } from 'primereact/keyfilter';
import { Password } from 'primereact/password';
import { Controller } from 'react-hook-form';
import { MdCheck, MdCheckCircle, MdCheckCircleOutline, MdInfo, MdOutlineCircle } from 'react-icons/md';
import Container from '../container/Container';
import { TextBodyExtraExtraSmall } from '../text/Texts';
import './form-input.styles.css';
import { cn } from '../ui/cn';
import { HTMLInputAutoCompleteAttribute } from 'react';

interface Props {
	className?: string;
	containerCls?: string;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	control: any;
	feedback?: boolean;
	formName: string;
	id: string;
	keyfilter?: KeyFilterType;
	label?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	value?: string;
	needValidIcon?: boolean;
	rules?: any; // Add this line
	autoComplete?: HTMLInputAutoCompleteAttribute;
}

const PasswordInput = ({
	className,
	containerCls,
	control,
	feedback = false,
	formName,
	id,
	keyfilter,
	label,
	onChange,
	placeholder,
	value,
	needValidIcon,
	autoComplete,
	rules
}: Props) => {
	const passwordRequirements = [
		{ regex: /.{8,}/, message: '8 or more characters' },
		{ regex: /[a-z]/, message: 'Lowercase letters' },
		{ regex: /[A-Z]/, message: 'Uppercase letters' },
		{ regex: /[0-9]/, message: 'At least one number' },
	];

	const getPasswordStrength = (value: string) => {
		if (!value?.length) {
			return '';
		}

		let strength = 0;
		if (value.length >= 8) strength++;
		if (/[a-z]/.test(value)) strength++;
		if (/[A-Z]/.test(value)) strength++;
		if (/[0-9]/.test(value)) strength++;

		if (strength === 4) return 'Strong';
		if (strength >= 2) return 'Medium';
		return 'Weak';
	};

	const renderHeader = (value: string) => {
		return (
			<div className="">
				<p className="text-base text-neutral0 font-normal mb-3">
					Your password must have:
				</p>
				{passwordRequirements.map((requirement, index) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						key={index}
						className={`flex items-center mb-1 text-body-medium ${value?.length
							? requirement.regex.test(value)
								? 'text-success'
								: 'text-error'
							: 'text-primaryFixed'
							}`}
					>
						{requirement.regex.test(value) ? (
							<MdCheckCircleOutline size={24} className="mr-2" />
						) : (
							<MdOutlineCircle size={24} className="mr-2" />
						)}
						<span>{requirement.message}</span>
					</div>
				))}
			</div>
		);
	};

	const renderFooter = (value: string) => {
		const passwordStrength = getPasswordStrength(value);

		return (
			<div className="mt-3">
				<p className="font-normal text-base text-neutral0 mb-2">
					Strength: {passwordStrength}
				</p>
				<div className="h-2 bg-gray-200">
					<div
						className={`h-full ${passwordStrength === 'Strong'
							? 'bg-success'
							: passwordStrength === 'Medium'
								? 'bg-warning'
								: passwordStrength === 'Weak'
									? 'bg-error'
									: ''
							}`}
						style={{
							width:
								passwordStrength === 'Strong'
									? '100%'
									: passwordStrength === 'Medium'
										? '66%'
										: passwordStrength === 'Weak'
											? '33%'
											: '0%',
						}}
					/>
				</div>
				<p className="text-sm text-neutral0 mt-2">
					Avoid using a password that you use with other websites or
					might be easy for someone else to guess.
				</p>
			</div>
		);
	};
	const idService = new IdGeneratorService(formName);
	const idGenerated = idService.generateFormInputIds(id);

	return (
		<Controller
			control={control}
			name={id}
			rules={rules}
			render={({ field, fieldState: { error } }) => {
				const currentValue = value ?? field.value;
				// Use the form's validation state - if no error and has value, it's valid
				const isValid = !error && currentValue && currentValue.length > 0;
				return (

					<Container className={cn('relative', containerCls)}>
						<FloatLabel>
							<Password
								autoComplete={autoComplete}
								inputId={idGenerated.id}
								data-testid={idGenerated.testId}
								className={`${className} ${error ? 'error-input' : ''}`}
								keyfilter={keyfilter}
								feedback={feedback}
								footer={
									feedback
										? renderFooter(value ?? field.value)
										: undefined
								}
								header={
									feedback
										? renderHeader(value ?? field.value)
										: undefined
								}
								inputClassName="w-full"
								onChange={field.onChange}
								placeholder={
									placeholder ?? idService.generateLabel(id)
								}
								pt={{
									root: () => ({
										className: 'password-container',
									}),
									input: () => ({
										className: 'password-input',
									}),
									meter: () => ({ className: 'meter-hide' }),
									meterLabel: () => ({
										className: 'meter-hide',
									}),
									info: () => ({ className: 'meter-hide' }),
									panel: () => ({
										className: 'custom-panel',
									}),
									showIcon: () => ({
										className: `password-iconField ${(needValidIcon && isValid) ? 'hidden' : ''}`,
									}),
									hideIcon: () => ({
										className: `password-iconField ${(needValidIcon && isValid) ? 'hidden' : ''}`,
									}),
								}}
								//Don't do that, this causes control re-render and focus loss: toggleMask={!isValid}
								toggleMask
								value={value ?? field.value}
							/>
							<label htmlFor={idGenerated.id}>
								{label ?? idService.generateLabel(id)}
							</label>
						</FloatLabel>
						{needValidIcon && isValid && (
							<div className={cn("absolute right-4 top-1/2 -translate-y-1/2 z-10")}>
								{<MdCheckCircle className="w-5 h-5 text-gray-400 text-success" />}
							</div>
						)}
						{error ? (
							<TextBodyExtraExtraSmall
								id={`${idGenerated.id}-helper`}
								className="block pl-[16px] mt-1 text-error"
								text={error?.message ?? ''}
							/>
						) : (
							''
						)}
					</Container>
				)
			}}
		/>
	);
};

export default PasswordInput;
